"use client";

import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";

import * as store from "./speech-store";

// ─── Markdown stripping ──────────────────────────────────────────────────────
//
// Agent replies are markdown. Fed raw to a screen reader, "**bold**" is read
// as "asterisk asterisk bold asterisk asterisk" and a URL is spelled out
// character by character. This strips the syntax a chat reply realistically
// contains while keeping the words a listener actually wants to hear.

export function stripMarkdownForSpeech(md: string): string {
  let text = md;

  // Fenced code blocks: drop entirely (code is not meant to be read aloud).
  text = text.replace(/```[\s\S]*?```/g, " ");

  // Inline code: keep the text inside the backticks.
  text = text.replace(/`([^`]+)`/g, "$1");

  // Images: ![alt](url) -> alt. MUST run before the link rule below: an image
  // contains a link as a substring, so the link rule would match the
  // "[alt](url)" inside it and leave a stray "!" behind.
  text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1");

  // Links: [text](url) -> text. Do this before stripping other emphasis
  // markers since link text itself may contain them.
  text = text.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1");

  // Headings: leading #'s.
  text = text.replace(/^#{1,6}\s+/gm, "");

  // Blockquote markers.
  text = text.replace(/^>\s?/gm, "");

  // Unordered list bullets.
  text = text.replace(/^\s*[-*+]\s+/gm, "");

  // Ordered list markers.
  text = text.replace(/^\s*\d+\.\s+/gm, "");

  // Bold/italic emphasis: ***x***, **x**, *x*, ___x___, __x__, _x_.
  text = text.replace(/(\*{1,3}|_{1,3})([^*_\n]+)\1/g, "$2");

  // Any leftover stray emphasis/heading/quote characters.
  text = text.replace(/[*_#>`]/g, "");

  // Collapse whitespace: multiple blank lines -> one, runs of spaces -> one.
  text = text.replace(/[ \t]+/g, " ");
  text = text.replace(/\n{2,}/g, "\n\n");
  text = text
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .replace(/\n{2,}/g, " ")
    .replace(/\n/g, " ")
    .replace(/ {2,}/g, " ")
    .trim();

  return text;
}

// ─── Chunking ────────────────────────────────────────────────────────────────
//
// Chrome silently truncates a single very long SpeechSynthesisUtterance, so
// long replies must be queued as several utterances. Split on sentence
// boundaries so a chunk never breaks mid-sentence when a boundary exists.

const DEFAULT_MAX_CHARS = 200;

export function chunkForSpeech(text: string, maxChars: number = DEFAULT_MAX_CHARS): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  // Split into sentences, keeping the terminating punctuation attached.
  const sentences = trimmed.match(/[^.!?]+[.!?]+(?=\s|$)|[^.!?]+$/g) ?? [trimmed];

  const chunks: string[] = [];
  let current = "";

  for (const raw of sentences) {
    const sentence = raw.trim();
    if (!sentence) continue;

    if (!current) {
      current = sentence;
      continue;
    }

    const candidate = `${current} ${sentence}`;
    if (candidate.length <= maxChars) {
      current = candidate;
    } else {
      chunks.push(current);
      current = sentence;
    }
  }

  if (current) chunks.push(current);

  return chunks.length > 0 ? chunks : [trimmed];
}

// ─── useSpeech hook ──────────────────────────────────────────────────────────
//
// A thin React binding over the module-level store in ./speech-store. All real
// state lives there because speechSynthesis is global; see that file's header.

export interface UseSpeechResult {
  supported: boolean;
  /** True when anything is playing, regardless of owner. */
  speaking: boolean;
  /** Which message is playing. Compare against your own id before showing "Stop". */
  speakingId: string | null;
  voices: SpeechSynthesisVoice[];
  selectedVoice: SpeechSynthesisVoice | null;
  autoSpeak: boolean;
  speak: (text: string, id?: string) => void;
  stop: () => void;
  selectVoice: (uri: string) => void;
  setAutoSpeak: (on: boolean) => void;
}

export function useSpeech(): UseSpeechResult {
  const state = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getServerSnapshot,
  );

  // An explicit choice wins. Otherwise fall back to the best available voice
  // rather than whatever the browser returns first, which is often a compact
  // voice and sometimes not even in the user's language.
  const selectedVoice =
    state.voices.find((v) => v.voiceURI === state.selectedVoiceURI) ??
    store.pickDefaultVoice(
      state.voices,
      typeof navigator !== "undefined" ? navigator.language : "en",
    );

  const speak = useCallback((text: string, id?: string) => {
    const snapshot = store.getSnapshot();
    // Tapping THIS control again while IT is speaking stops it (toggle).
    // Scoped by id: clicking Listen on a different message must switch to it in
    // one click, not merely cancel whatever was playing.
    const owner = id ?? null;
    if (snapshot.speaking && snapshot.speakingId === owner) {
      store.stop();
      return;
    }
    store.speak(chunkForSpeech(text), owner);
  }, []);

  return {
    supported: state.supported,
    speaking: state.speaking,
    speakingId: state.speakingId,
    voices: state.voices,
    selectedVoice,
    autoSpeak: state.autoSpeak,
    speak,
    stop: store.stop,
    selectVoice: store.selectVoice,
    setAutoSpeak: store.setAutoSpeak,
  };
}

/**
 * Speaks each new finished assistant reply while auto-speak is on.
 *
 * Call this ONCE PER TRANSCRIPT SOURCE, never per renderer. A transcript has one
 * source of truth (ChatContext for 1:1, room messages for rooms) and may have
 * several renderers — /chat's MessageList and the global drawer render the very
 * same array. Calling it in each renderer makes two consumers speak the same
 * reply twice, the second cancelling and re-queueing the first.
 */
export function useAutoSpeak(
  messages: readonly store.AutoSpeakCandidate[],
  conversationId: string | null = null,
): void {
  // Subscribe to the boolean, not the whole snapshot: every playback transition
  // replaces the state object, and re-rendering the entire transcript (and its
  // markdown) on each one is pure waste. A primitive compares by value.
  const autoSpeak = useSyncExternalStore(
    store.subscribe,
    () => store.getSnapshot().autoSpeak,
    () => false,
  );
  const lastHandledRef = useRef<string | null>(null);
  // Which conversation we are primed against. Priming is keyed on conversation
  // identity, NOT on mount: MessageList is not remounted or re-keyed when you
  // switch conversations, so a mount-only prime carries the previous
  // conversation's baseline into the new one and reads its last reply at you.
  const primedForRef = useRef<string | null | undefined>(undefined);
  const spokenIdsRef = useRef<Set<string>>(new Set());
  // Ids this transcript owns, for the unmount cleanup below. Written in the
  // effect, never during render: a render React discards (concurrent interrupt,
  // StrictMode) must not leave the ref describing a commit that never happened.
  const ownedIdsRef = useRef<Set<string>>(new Set());
  // The message set seen on the previous effect pass, to tell a conversation
  // SWITCH (wholesale replacement) from a conversation being ASSIGNED AN ID
  // (same thread, messages appended) — see the prime branch below.
  const prevIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!store.supportsSpeech()) return;

    const currentIds = new Set(messages.map((m) => m.id));
    ownedIdsRef.current = currentIds;

    // Prime on first sight of a conversation: whatever is already there counts
    // as heard. An empty list is not a conversation yet — wait for its history,
    // otherwise switching primes against [] and then speaks the reply that loads.
    if (primedForRef.current !== conversationId) {
      if (messages.length === 0) return;

      // A conversation id can change for two very different reasons, and
      // treating them alike swallows replies. On a real SWITCH the message set
      // is replaced. But a brand-new conversation gets its id assigned BY the
      // first response, arriving in the same commit as that reply — there the
      // set merely grew, and the reply must still be spoken.
      const isSameThread = store.isSameThreadContinuation(prevIdsRef.current, currentIds);

      primedForRef.current = conversationId;
      prevIdsRef.current = new Set(currentIds);

      if (!isSameThread) {
        spokenIdsRef.current = new Set();
        const existing = store.pickAutoSpeakTarget(messages, null);
        lastHandledRef.current = existing?.id ?? null;
        return;
      }
      // Same thread, newly identified — fall through and speak normally.
    }

    prevIdsRef.current = new Set(currentIds);

    if (!autoSpeak) return;

    const target = store.pickAutoSpeakTarget(messages, lastHandledRef.current);
    if (!target || !target.content) return;
    // Belt and braces against a re-speak if an id ever recurs in a list.
    if (spokenIdsRef.current.has(target.id)) return;

    lastHandledRef.current = target.id;
    spokenIdsRef.current.add(target.id);
    store.speak(chunkForSpeech(stripMarkdownForSpeech(target.content)), target.id);
  }, [messages, conversationId, autoSpeak]);

  // Leaving stops playback we own, so no voice outlives the Stop button that
  // unmounted with it. Scoped by id: an unconditional stop() here would cut off
  // the global chat drawer, which stays mounted across route changes and has
  // its own Listen buttons.
  useEffect(
    () => () => {
      const playing = store.getSnapshot().speakingId;
      // Covers both auto-spoken replies and ones the user tapped Listen on:
      // anything rendered by THIS surface is ours to silence.
      if (playing && ownedIdsRef.current.has(playing)) store.stop();
    },
    [],
  );

  // Unmount is not enough. The 1:1 owner is ChatProvider, which lives in the
  // workspace layout and never unmounts on in-app navigation — so leaving /chat
  // mid-reply used to leave audio playing with no Stop button and no toggle
  // anywhere on screen. A route change is the lifetime that actually ends here.
  const pathname = usePathname();
  const lastPathRef = useRef(pathname);
  useEffect(() => {
    if (lastPathRef.current === pathname) return;
    lastPathRef.current = pathname;
    const playing = store.getSnapshot().speakingId;
    if (playing && ownedIdsRef.current.has(playing)) store.stop();
  }, [pathname]);
}
